import { v4 as uuidv4 } from 'uuid'

export namespace Utility {
  export function sleep(milliseconds: number): Promise<void> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve()
      }, milliseconds)
    })
  }

  export function isNull(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value?.trim() === '')
    )
  }

  export function getUUID(): string {
    return uuidv4()
  }

  export function isDefined(value: any): boolean {
    const isEmptyString = typeof value === 'string' && value === ''
    return value !== null && value !== undefined && !isEmptyString
  }

  export function isEmpty<Type>(value: Type | Type[]): boolean {
    if (!isDefined(value)) {
      return true
    }

    const isArray = Array.isArray(value)

    if (isArray) {
      return value.length === 0
    }

    const isString = typeof value === 'string'

    if (isString) {
      return value.trim() === ''
    }

    return false
  }

  export function arrayUnique<Type>(items: Type[]): Type[] {
    const uniqueSet = new Set(items)
    return Array.from(uniqueSet)
  }

  export function stringify(values: any): string {
    if (typeof values === 'string') {
      return values
    }

    return JSON.stringify(values, null, 2)
  }

  export function stringifyForce(value: any): string {
    try {
      const result = stringify(value)
      return result
    } catch {
      const valueSafe = prepareStringify(value)
      const result = JSON.stringify(valueSafe)

      return result
    }
  }

  export function stringSanitizeUnsafeChars(value: string): string {
    return (
      value
        // eslint-disable-next-line no-control-regex
        ?.replace(/\u0000/g, '') // Remove null characters
        // eslint-disable-next-line no-control-regex
        ?.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters
        // .replace(/\\/g, '\\\\') // Escape backslashes
        // .replace(/\r/g, '\n') // Normalize carriage returns
        // .replace(/\s+/g, ' ') // Normalize whitespace
        ?.trim()
    ) // Trim spaces
  }

  export function slugify(name: string) {
    return name
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace whitespace with dash
  }

  export function prepareStringify(object) {
    if (Array.isArray(object)) {
      return object
        .map(prepareStringify)
        .filter(value => value !== null && value !== undefined)
    } else if (object && typeof object === 'object') {
      return Object.fromEntries(
        Object.entries(object)
          .filter(([key, value]) => value !== null && value !== undefined)
          .map(([key, value]) => [key, prepareStringify(value)]),
      )
    } else if (typeof object === 'bigint') {
      return object.toString()
    } else if (Buffer.isBuffer(object)) {
      return '[Buffer]'
    } else if (typeof object === 'symbol') {
      return object.toString()
    } else if (typeof object === 'function') {
      return '[Function]'
    }
    return object
  }

  export function openInNewTab(window: Window, url: string): void {
    window.open(url, '_blank')
  }

  export function sortByString<Type>(items: Type[], key: keyof Type): Type[] {
    return items.sort((a: Type, b: Type) =>
      (a[key] as string).localeCompare(b[key] as string),
    )
  }

  export function removeTrailingSlash(content: string): string {
    const REGEX_SLASH = /\/$/g

    return content.replace(REGEX_SLASH, '')
  }

  export function stringToInitials(content: string): string {
    if (isNull(content)) {
      return ''
    }

    const words = content.trim().split(' ')

    const isOneWord = words.length === 1

    if (isOneWord) {
      return words[0].slice(0, 2)?.toUpperCase()
    }

    return `${words[0][0]}${words[1][0]}`.toUpperCase()
  }

  export function hashStringToRange(
    str: string,
    min: number,
    max: number,
  ): number {
    const hashBuffer = new TextEncoder().encode(str)
    let hash = 0

    for (let i = 0; i < hashBuffer.length; i++) {
      hash = (hash * 31 + hashBuffer[i]) >>> 0 // simple hash function
    }

    const range = max - min + 1
    return min + (hash % range)
  }

  export function stringToOneLetter(content: string): string {
    if (isNull(content)) {
      return ''
    }

    const words = content.trim().split(' ')

    const isOneWord = words.length === 1

    if (isOneWord) {
      return words[0].slice(0, 1)?.toUpperCase()
    }

    return `${words[0][0]}`.toUpperCase()
  }

  export function debounce<FunctionType extends (...args: any[]) => void>(
    func: FunctionType,
    delay: number,
  ): FunctionType {
    let timeoutId: ReturnType<typeof setTimeout>

    return function (...args: Parameters<FunctionType>) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      timeoutId = setTimeout(() => {
        func(...args)
      }, delay)
    } as FunctionType
  }

  export function getRandomNumber(min: number, max: number): number {
    if (min > max) {
      throw new Error('min must be less than or equal to max')
    }
    return Math.random() * (max - min) + min
  }

  export async function mapAsync<Type, Response>(
    items: Type[],
    handler: (item: Type, index: number) => Promise<Response>,
  ): Promise<Response[]> {
    const promises = items.map((item, index) => handler(item, index))

    const result = await Promise.all(promises)

    return result
  }

  /**
   * ? ChatGPT generated
   */
  export function stringToRandomNumber(
    input: string,
    min: number,
    max: number,
  ): number {
    let hash = 0
    for (let i = 0; i < input.length; i++) {
      hash = (hash << 5) - hash + input.charCodeAt(i)
      hash |= 0
    }

    const normalized = (Math.abs(hash) % 1000) / 1000

    return Math.floor(normalized * (max - min + 1)) + min
  }

  /**
   * Returns true if the pattern match the key.
   *
   * Example: Pattern "web-*" matches with key "web-1"
   */
  export function isPattern(pattern: string, key: string): boolean {
    const items = pattern.split('*')

    const keyStart = items[0] ?? ''
    const keyEnds = items.slice(-1)[0] ?? ''
    const keyMotifs = items.slice(1, -1)

    if (!key.startsWith(keyStart)) {
      return false
    }

    if (!key.endsWith(keyEnds)) {
      return false
    }

    let itemAnalyzed = key

    for (const keyMotif of keyMotifs) {
      if (!itemAnalyzed.includes(keyMotif)) {
        return false
      }

      itemAnalyzed = itemAnalyzed.split(keyMotif).slice(1).join(keyMotif)
    }

    return true
  }

  export function createHash(input: string): string {
    let hash = 0

    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash |= 0 // Convert to 32bit integer
    }

    return Math.abs(hash).toString(16)
  }

  /**
   * Batch async operations. Similar to Promise.all, but with a maximum batch size. Returns a promise that resolves to an array of the results of the promises passed as an argument.
   *
   * On error, the batch will be aborted and the error will be thrown. Subsequent batches will not be executed.
   *
   * @param promises The promises to batch
   * @param batchSize The maximum batch size
   * @param beforeBatch A function to call before each batch
   * @param afterBatch A function to call after each batch
   */
  export async function batch<DataType>(
    promiseBuilders: (() => Promise<DataType>)[],
    batchSize: number = 100,
    beforeBatch?: (
      countBatches: number,
      countItems: number,
      index: number,
    ) => any,
    afterBatch?: (
      countBatches: number,
      countItems: number,
      index: number,
    ) => any,
  ): Promise<DataType[]> {
    const results: DataType[] = []

    const countBatches = Math.ceil(promiseBuilders.length / batchSize)

    for (let index = 0; index < promiseBuilders.length; index += batchSize) {
      const batch = promiseBuilders
        .slice(index, index + batchSize)
        .map(builder => builder())

      const countItems = batch.length
      const batchIndex = index / batchSize

      if (beforeBatch) {
        await beforeBatch(countBatches, countItems, batchIndex)
      }

      try {
        const batchResults = await Promise.all(batch)
        results.push(...batchResults)
      } finally {
        if (afterBatch) {
          await afterBatch(countBatches, countItems, batchIndex)
        }
      }
    }

    return results
  }

  export function stringToHexColor(input: string = 'unkown'): string {
    const hash = createHash(input)
    // Extract the first 6 characters to form a valid hex color
    return `#${hash.substring(0, 6)}`
  }

  export function stringToGradient(input: string = 'unknown'): string {
    const hash = createHash(input)
    const hashExtended = hash.repeat(Math.ceil(12 / hash.length)).slice(0, 12)
    // Extract portions of the hash for two colors
    const color1 = `#${hashExtended.substring(0, 6)}`
    const color2 = `#${hashExtended.substring(6, 12)}`
    const angle = parseInt(hash, 16) % 360

    // Return a linear gradient CSS string
    return `linear-gradient(${angle}deg, ${color1}, ${color2})`
  }

  export function stringToColorPalette(input: string = 'unknown'): string {
    const colors = [
      '#1f3a61',
      '#497c92',
      '#6ec1c4',
      '#f9a64e',
      '#e83f21',
      '#e17256',
      '#6b5be6',
      '#5da6e1',
      '#fd77a6',
      '#00b893',
    ]
    const hash = createHash(input)
    const index = parseInt(hash, 16) % colors.length

    return colors[index]
  }

  export function flattenObject(
    values: Record<string, any>,
    parentKey = '',
    result: Record<string, any> = {},
  ): Record<string, any> {
    if (!values) {
      return values
    }

    for (const key of Object.keys(values)) {
      const keyFull = parentKey ? `${parentKey}.${key}` : key
      const value = values[key]

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        flattenObject(value, keyFull, result) // Recursively flatten nested objects
      } else {
        result[keyFull] = value // Add non-object values directly
      }
    }
    return result
  }

  export function unflattenObject(
    flatObject: Record<string, any>,
  ): Record<string, any> {
    const result: Record<string, any> = {}

    for (const [key, value] of Object.entries(flatObject)) {
      const keys = key.split('.')
      let current = result

      for (let i = 0; i < keys.length; i++) {
        const part = keys[i]

        if (i === keys.length - 1) {
          // Last key, set the value
          current[part] = value
        } else {
          // Ensure the next level exists as an object
          if (!current[part] || typeof current[part] !== 'object') {
            current[part] = {}
          }
          current = current[part]
        }
      }
    }

    return result
  }

  export function trimNullValues(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(trimNullValues).filter(v => v !== null && v !== undefined)
    } else if (obj && typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([_, v]) => v !== null && v !== undefined)
          .map(([k, v]) => [k, trimNullValues(v)]),
      )
    }
    return obj
  }

  export function toCamelCase(content: string): string {
    const REGEX_UPPERCASE = /^[A-Z]+$/g
    const isAllUpperCase = REGEX_UPPERCASE.test(content)

    if (isAllUpperCase) {
      return content.toLowerCase()
    }

    return content.replace(/_([a-zA-Z0-9])/g, (_, match) => match.toUpperCase())
  }

  export function lowerFirstLetter(str: string): string {
    if (!str) return '' // Handle empty string case
    return str.charAt(0).toLowerCase() + str.slice(1)
  }

  export function capitalizeFirstLetter(str: string): string {
    if (!str) return '' // Handle empty string case
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  export function toLowerCamelCase(content: string): string {
    return lowerFirstLetter(toCamelCase(content))
  }

  export function pickRandomSet<ValueType>(values: ValueType[], count: number) {
    const options: ValueType[] = []

    const valuesUpdated = [...values]

    for (let index = 0; index < count; index++) {
      const indexValue = Math.floor(getRandomNumber(0, valuesUpdated.length))

      options.push(valuesUpdated[indexValue])

      valuesUpdated.splice(indexValue, 1)

      if (valuesUpdated.length === 0) {
        break
      }
    }

    return options
  }

  export function isEmail(email: string): boolean {
    if (!email || typeof email !== 'string') return false

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  export async function retryOnce<T>(
    fn: () => Promise<T>,
    delay: number = 500,
  ): Promise<T> {
    try {
      const result = await fn()

      return result
    } catch (error) {
      console.error(`${fn.name} failed. Retrying in ${delay}ms...`)
      console.error(error.message)

      await sleep(delay)

      const result = await fn()

      return result
    }
  }
}
